import { createRouter, createWebHistory } from 'vue-router'
import { routes } from '@/config/routes/customRoutes'
import store from '../store'
import AuthenticationService from '../services/authentication/AuthenticationService'

// Process routes to handle layouts
const processedRoutes = routes.map(route => {
  const meta = route.meta || {}

  if (meta.layout) {
    return {
      path: route.path,
      component: meta.layout,
      meta: {
        requiresAuth: meta.scope === 'private',
        guest: meta.guest || false
      },
      children: [
        {
          path: '',
          name: route.name,
          component: route.component,
          meta: {
            ...meta,
            requiresAuth: meta.scope === 'private',
            guest: meta.guest || false,
            roles: meta.roles || []
          }
        }
      ]
    }
  }

  return {
    ...route,
    meta: {
      ...meta,
      requiresAuth: meta.scope === 'private',
      guest: meta.guest || false,
      roles: meta.roles || []
    }
  }
})

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes: processedRoutes
})

// Enhanced navigation guard with impersonation support
router.beforeEach(async (to, from, next) => {
  // Get the actual route meta (handle nested routes)
  const routeMeta = to.matched.length > 0
    ? to.matched[to.matched.length - 1].meta
    : to.meta

  // Public routes - always allow
  if (!routeMeta.requiresAuth) {
    return next()
  }

  // Check authentication using AuthenticationService
  const isAuthenticated = AuthenticationService.isAuthenticated()

  if (!isAuthenticated) {
    return next({
      path: '/login',
      query: { redirect: to.fullPath }
    })
  }

  // Check if we're impersonating
  const impersonationActive = localStorage.getItem('impersonation_active') === 'true'

  if (impersonationActive) {
    // Make sure we have impersonated user data in store
    const user = store.getters['auth/user']
    const isImpersonating = store.getters['auth/isImpersonating']

    if (!user || !isImpersonating) {
      try {
        await store.dispatch('auth/fetchUser')
      } catch (error) {
        console.error('Failed to fetch impersonated user:', error)

        // If it's an auth error, clear everything and redirect to login
        if (error?.response?.status === 401) {
          AuthenticationService.clearToken()
          localStorage.removeItem('impersonation_active')
          return next({
            path: '/login',
            query: { redirect: to.fullPath }
          })
        }
      }
    }
  }

  // Get current user from store
  const user = store.getters['auth/user']

  // If user not in store yet, load it
  if (!user) {
    const stillAuthenticated = await store.dispatch('auth/checkAuthStatus')

    if (!stillAuthenticated) {
      return next({
        path: '/login',
        query: { redirect: to.fullPath }
      })
    }
  }

  // Check role-based access for authenticated routes
  if (routeMeta.roles && routeMeta.roles.length > 0) {
    const currentUser = store.getters['auth/user']
    const userRole = currentUser?.roles && currentUser.roles.length > 0
      ? currentUser.roles[0].name
      : null

    if (!userRole || !routeMeta.roles.includes(userRole)) {
      return next('/unauthorized')
    }
  }

  // Check if route is for guests only
  if (routeMeta.guest && isAuthenticated) {
    return next('/my-profile')
  }

  next()
})

// Update page title after navigation
router.afterEach((to) => {
  const routeMeta = to.matched.length > 0
    ? to.matched[to.matched.length - 1].meta
    : to.meta

  document.title = routeMeta.title
    ? `${routeMeta.title} - EDCTP`
    : 'EDCTP Alumni Network'
})

// Handle router errors
router.onError((error) => {
  console.error('Router error:', error)
})

export default router
